import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRevenueStructure } from '@/hooks/useFinanceAnalytics';

// Color palette for cost elements
const COLORS = [
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f97316', // orange
  '#22c55e', // green
  '#06b6d4', // cyan
  '#eab308', // yellow
  '#6366f1', // indigo
];

export default function RevenueStructureChart({ branchId, startDate, endDate }) {
  const { data: structureData, isLoading, error } = useRevenueStructure(branchId, startDate, endDate);

  // Transform data for chart; compute percentage from displayed total so proportion is always correct
  const chartData = React.useMemo(() => {
    if (!structureData || structureData.length === 0) return [];

    const totalCents = structureData.reduce((sum, item) => sum + Number(item.total_cents || 0), 0);
    const totalDisplay = totalCents / 100;

    return structureData.map((item, index) => {
      const valueCents = Number(item.total_cents || 0);
      const value = valueCents / 100;
      const percentage = totalCents > 0 ? (valueCents / totalCents) * 100 : 0;
      return {
        name: item.cost_element_name,
        value,
        percentage,
        color: COLORS[index % COLORS.length]
      };
    });
  }, [structureData]);

  // Total for reference (e.g. tooltip)
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0]?.payload;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900 mb-1">{data?.name}</p>
        <div className="space-y-1 text-sm">
          <p className="text-slate-600">
            Kwota: <span className="font-medium">{data?.value?.toLocaleString('pl-PL')} PLN</span>
          </p>
          <p className="text-slate-600">
            Udział: <span className="font-medium">{data?.percentage?.toFixed(1)}%</span>
          </p>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-500">Nie udało się załadować struktury przychodów</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Struktura przychodów</CardTitle>
        <CardDescription>
          Udział poszczególnych elementów kosztowych w przychodach
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[200px] text-slate-500">
            Brak danych o strukturze przychodów
          </div>
        ) : (
          <div className="space-y-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                layout="vertical"
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  width={120}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Legend with percentages */}
            <div className="flex flex-wrap gap-4 justify-center">
              {chartData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-slate-600">
                    {item.name}: <span className="font-medium">{item.percentage.toFixed(1)}%</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
